import { queryOptions } from "@tanstack/react-query";
import type {
  CreateLocationBudgetPeriodInput,
  CreateLocationInput,
  DocumentTypeId,
  LocationBudgetPeriodDto,
  LocationDto,
  LocationId,
  LocationPickerDto,
  PaginatedResponse,
  PaginationParams,
  UpdateLocationInput,
} from "@yalana/contracts";
import { fetchApi } from "../../../shared/infrastructure/api-client";
import { toListSearchParams } from "../../../shared/utils/list-search-params";

export const locationQueries = {
  all: () => ["locations"] as const,
  list: (
    params: PaginationParams & {
      customerId?: string;
      q?: string;
      sort?: string;
      dir?: "asc" | "desc";
    } = { page: 1, pageSize: 10 },
  ) =>
    queryOptions({
      queryKey: [...locationQueries.all(), "list", params] as const,
      queryFn: () =>
        fetchApi<PaginatedResponse<LocationDto>>("/api/locations", {
          searchParams: toListSearchParams({
            page: params.page,
            pageSize: params.pageSize,
            customerId: params.customerId,
            q: params.q,
            sort: params.sort,
            dir: params.dir,
          }),
        }),
    }),
  // listAll: fetches up to 500 locations — for use in dropdowns/selects
  listAll: (params: { customerId?: string } = {}) =>
    queryOptions({
      queryKey: [...locationQueries.all(), "listAll", params] as const,
      queryFn: () => {
        const sp = new URLSearchParams({ page: "1", pageSize: "500" });
        if (params.customerId) sp.set("customerId", params.customerId);
        return fetchApi<PaginatedResponse<LocationDto>>(
          `/api/locations?${sp.toString()}`,
        ).then((r) => r.data);
      },
    }),
  // picker: minimal-fields listing gated by requests.write (not admin.locations.read) —
  // for request-builder selects and the welcome dashboard. Admin location screens
  // still use listAll()/list() for the full LocationDto.
  picker: (params: { customerId?: string } = {}) =>
    queryOptions({
      queryKey: [...locationQueries.all(), "picker", params] as const,
      queryFn: () => {
        const sp = new URLSearchParams();
        if (params.customerId) sp.set("customerId", params.customerId);
        return fetchApi<LocationPickerDto[]>(
          `/api/locations/picker?${sp.toString()}`,
        );
      },
    }),
  detail: (locationId: LocationId) =>
    queryOptions({
      queryKey: [...locationQueries.all(), locationId] as const,
      queryFn: () => fetchApi<LocationDto>(`/api/locations/${locationId}`),
      enabled: !!locationId,
    }),
  documentTypes: (locationId: LocationId) =>
    queryOptions({
      queryKey: [
        ...locationQueries.all(),
        locationId,
        "document-types",
      ] as const,
      queryFn: () =>
        fetchApi<DocumentTypeId[]>(
          `/api/locations/${locationId}/document-types`,
        ),
      enabled: !!locationId,
    }),
  budgetPeriods: (locationId: LocationId) =>
    queryOptions({
      queryKey: [...locationQueries.all(), locationId, "budget"] as const,
      queryFn: () =>
        fetchApi<LocationBudgetPeriodDto[]>(
          `/api/locations/${locationId}/budget`,
        ),
      enabled: !!locationId,
    }),
};

export const locationMutations = {
  create: () => ({
    mutationFn: (data: CreateLocationInput) =>
      fetchApi<LocationDto>("/api/locations", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  }),
  update: (locationId: LocationId) => ({
    mutationFn: (data: UpdateLocationInput) =>
      fetchApi<LocationDto>(`/api/locations/${locationId}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
  }),
  delete: (locationId: LocationId) => ({
    mutationFn: () =>
      fetchApi<void>(`/api/locations/${locationId}`, {
        method: "DELETE",
      }),
  }),
  deactivate: (locationId: LocationId) => ({
    mutationFn: () =>
      fetchApi<void>(`/api/locations/${locationId}/deactivate`, {
        method: "PATCH",
      }),
  }),
  reactivate: (locationId: LocationId) => ({
    mutationFn: () =>
      fetchApi<void>(`/api/locations/${locationId}/reactivate`, {
        method: "PATCH",
      }),
  }),
  setDocumentTypes: (locationId: LocationId) => ({
    mutationFn: (documentTypeIds: DocumentTypeId[]) =>
      fetchApi<void>(`/api/locations/${locationId}/document-types`, {
        method: "PUT",
        body: JSON.stringify({ documentTypeIds }),
      }),
  }),
  createBudgetPeriod: (locationId: LocationId) => ({
    mutationFn: (data: CreateLocationBudgetPeriodInput) =>
      fetchApi<LocationBudgetPeriodDto>(`/api/locations/${locationId}/budget`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
  }),
};
